import { getConfig, getEndpoints } from './config.js';

/**
 * Lightswitch SDK Core Tracking
 * 
 * Handles anonymous usage event tracking for analytics purposes.
 * Pure JavaScript implementation with no framework dependencies.
 */


/**
 * Get user agent string safely
 * @returns {string} User agent string
 */
const getUserAgent = () => {
  if (typeof navigator !== 'undefined' && navigator.userAgent) {
    return navigator.userAgent;
  }
  return 'Unknown';
};

/**
 * Get current path safely
 * @returns {string} Current path
 */
const getCurrentPath = () => {
  if (typeof window !== 'undefined' && window.location) {
    return window.location.pathname + window.location.search;
  }
  return '/';
};

/**
 * Get referrer safely
 * @returns {string} Referrer URL or empty string if not available
 */
const getReferrer = () => {
  if (typeof document !== 'undefined' && document.referrer) {
    return document.referrer;
  }
  return '';
};

/**
 * Track a usage event for analytics purposes
 * @param {string} eventType - Type of event being tracked
 * @param {Object} [params] - Additional parameters to track
 * @param {Object} [options={}] - Options for the tracking request
 * @returns {Promise<Object>} Tracking result object
 */
const trackEvent = async function (eventType) {
  let params = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  let options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  try {
    const config = getConfig();
    const endpoints = getEndpoints();
    if (!config.publishableKey) {
      throw new Error('No publishable key configured');
    }

    // Prepare the event data with defaults
    const trackingData = {
      event_type: eventType,
      path: getCurrentPath(),
      user_agent: getUserAgent(),
      referrer: getReferrer(),
      timestamp: Date.now(),
      params: params || null
    };
    console.log(`Tracking event: ${trackingData.event_type} at ${trackingData.path}`);
    const response = await fetch(endpoints.track, {
      method: 'POST',
      headers: {
        'X-Publishable-Key': config.publishableKey,
        'X-App-Slug': config.appSlug || 'default',
        'Content-Type': 'application/json',
        ...options.headers
      },
      body: JSON.stringify(trackingData)
    });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    const result = await response.json();
    console.log(`Event tracked successfully:`, result);
    return {
      status: result?.status || 'ok',
      event_type: trackingData.event_type,
      path: trackingData.path,
      timestamp: trackingData.timestamp,
      error: null,
      ...result
    };
  } catch (error) {
    console.error('Error tracking event:', error);
    return {
      status: 'error',
      event_type: eventType,
      path: getCurrentPath(),
      error: error.message || 'Failed to track event'
    };
  }
};

export { trackEvent };
//# sourceMappingURL=tracking.js.map
